import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import BASE_URL from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";

const TrialFeatureSection = () => {
  const [cropType, setCropType] = useState("plantation");
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const navigate = useNavigate();

  const trialFeatures = [
    "Limited Fertilizer & Nutrient Tips Plus Basic Farm Dashboard",
    "Farm Management for up to 2 Hectares",
    "1-Month Trial Period",
  ];

  // Fetch crops based on crop type
  useEffect(() => {
    const fetchCrops = async () => {
      const controller = new AbortController();
      const signal = controller.signal;
      setLoading(true);

      try {
        const response = await fetch(
          `${BASE_URL}/api/${cropType === "plantation" ? "plantation-crops" : "field-crops"}`,
          { signal }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch crops: ${response.statusText}`);
        }

        const data = await response.json();
        if (Array.isArray(data)) {
          const formattedCrops = data.map((crop) => ({
            id: crop.crop_id,
            name: crop.crop_name,
          }));
          setCrops(formattedCrops);
          if (formattedCrops.length > 0) {
            setSelectedCrop(formattedCrops[0].name);
          }
        } else {
          console.error("Unexpected crop data format", data);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching crops:", error);
        }
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    };

    fetchCrops();
  }, [cropType]);

  // Handle trial transaction creation
  const handleStartTrial = async () => {
    try {
      const email = document.cookie
        .split("; ")
        .find((row) => row.startsWith("user_email="))
        ?.split("=")[1];

      if (!email) {
        navigate("/user/login");
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/api/payment/check-trial-eligibility`,
        { email }
      );

      if (response.data.eligible) {
        // Proceed to add farm or continue flow
        navigate("/user/trial-add-farm");
      } else {
        setShowTrialPopup(true);
      }
    } catch (error) {
      console.error("Trial check error:", error);
      alert("Something went wrong while checking trial eligibility.");
    }
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#E8F5E9",
          borderRadius: "16px",
          padding: { xs: 3, sm: 5 },
          marginY: { xs: 4, sm: 8 },
          boxShadow: "0px 6px 12px rgba(0, 128, 0, 0.15)",
          textAlign: "center",
          border: "3px solid #4CAF50",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: "#2E7D32",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "1px",
            marginBottom: 2,
          }}
        >
          🎯 Try Before You Buy!
        </Typography>

        <Typography
          sx={{
            color: "#555",
            marginBottom: 4,
            fontSize: { xs: "1rem", sm: "1.2rem" },
          }}
        >
          Experience our powerful agricultural solutions with a{" "}
          <b>FREE TRIAL</b>! Here's what you get:
        </Typography>

        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={10} md={8}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, sm: 3 },
                backgroundColor: "#F1F8E9",
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <List>
                {trialFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ paddingY: 1 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: "#4CAF50" }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} sx={{ color: "#2E7D32" }} />
                  </ListItem>
                ))}
              </List>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                {/* <FormControl>
                  <FormLabel>Select Crop Type</FormLabel>
                  <RadioGroup
                    row
                    value={cropType}
                    onChange={(e) => setCropType(e.target.value)}
                  >
                    <FormControlLabel
                      value="plantation"
                      control={<Radio color="success" />}
                      label="Plantation"
                    />
                    <FormControlLabel
                      value="field"
                      control={<Radio color="success" />}
                      label="Field Crop"
                    />
                  </RadioGroup>
                </FormControl> */}

                {/* {loading ? (
                  <CircularProgress color="success" />
                ) : (
                  <FormControl fullWidth>
                    <Select
                      value={selectedCrop}
                      onChange={(e) => setSelectedCrop(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select a Crop
                      </MenuItem>
                      {crops.length === 0 ? (
                        <MenuItem disabled>No crops available</MenuItem>
                      ) : (
                        crops.map((crop) => (
                          <MenuItem key={crop.id} value={crop.name}>
                            {crop.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )} */}

                <Button
                  variant="contained"
                  color="success"
                  onClick={handleStartTrial}
                >
                  Start Your Free Trial
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* 409 Trial Used Popup */}
      <Dialog open={showTrialPopup} onClose={() => setShowTrialPopup(false)}>
        <DialogTitle>Trial Already Used</DialogTitle>
        <DialogContent>
          You've already made a transaction, so you're not eligible for a trial
          at this time.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigate("/user/crop-health-management")}
            color="primary"
          >
            Go to Dashboard
          </Button>
          <Button onClick={() => setShowTrialPopup(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrialFeatureSection;
